//
// Copyright (c) 2011-2024 by Curalate, Inc.
//

import React, { useCallback, useEffect } from 'react';
import { useFormContext, useWatch } from 'react-hook-form';
import styled from 'styled-components';

import { FulfillmentType } from '@apis/campaign-requests/sampling/campaign-requests/samplingCampaignRequestsApiModel';
import { CampaignRequest } from '@apis/campaign-requests/shared/CampaignRequests';
import { CAMPAIGNS_TRACKING_ID } from '@campaigns/constants/CampaignsTrackingId';
import {
  FULFILLMENT_TYPE_DEALS,
  FULFILLMENT_TYPE_SHIPPED,
} from '@campaigns/constants/sampling/SamplingCampaignRequestConstants';
import { isOneOffCreatorRequest } from '@campaigns/utils/sampling/SamplingCampaignRequestUtils';
import HelpIcon from '@components/atoms/help-icon/HelpIcon';
import Icon from '@components/atoms/icon/Icon';
import { BodyText, CaptionErrorText } from '@components/atoms/text/Text';
import { FormFieldController } from '@components/form/field-controller/FormFieldController';
import { RadioGroup } from '@components/primitives/radio-group/RadioGroup';

const radioItems = [
  {
    value: FULFILLMENT_TYPE_SHIPPED,
    label: 'Shipped products',
    trackingId:
      CAMPAIGNS_TRACKING_ID.CREATE_CAMPAIGN_DELIVERY_SHIPPED_RADIO_ITEM,
  },
  {
    value: FULFILLMENT_TYPE_DEALS,
    label: 'Coupon codes',
    trackingId:
      CAMPAIGNS_TRACKING_ID.CREATE_CAMPAIGN_DELIVERY_COUPON_RADIO_ITEM,
  },
];

interface CampaignRequestRadioFieldProps {
  isDisabled?: boolean;
}

export const CampaignRequestDeliveryField = ({
  isDisabled,
}: CampaignRequestRadioFieldProps) => {
  const { control, getValues, setValue, unregister } =
    useFormContext<CampaignRequest>();

  const type = useWatch({ control, name: 'type' });
  const communityType = useWatch({ control, name: 'communityType' });
  const fulfillmentType = useWatch({
    control,
    name: 'fulfillmentOptions.fulfillmentType',
  });

  // Coupon codes are not offered for one-off creator campaigns.
  const isOneOffCreator = isOneOffCreatorRequest(type, communityType);
  const deliveryOptions = isOneOffCreator
    ? radioItems.filter((item) => item.value !== FULFILLMENT_TYPE_DEALS)
    : radioItems;

  const handleDeliveryMethodChange = useCallback(
    (value: FulfillmentType) => {
      unregister('fulfillmentOptions.whoShipsProduct');
      unregister('fulfillmentOptions.codeSupplier');
      setValue('comments.shipping', undefined);

      setValue('fulfillmentOptions', {
        fulfillmentType: value,
        boxType: undefined,
        whoShipsProduct: undefined,
        emails:
          value === FULFILLMENT_TYPE_SHIPPED
            ? getValues('fulfillmentOptions.emails')
            : undefined,
        riskManagement: undefined,
        codeScope: undefined,
        clientCodes: undefined,
        clientCodesSupplied: undefined,
        codeSupplier: undefined,
        codeType: undefined,
        codeValue: undefined,
        redemptionType: undefined,
        retailers: undefined,
      });
    },
    [getValues, setValue, unregister],
  );

  // Switching community to creator after picking Coupon codes would otherwise leave
  // a hidden `deals` selection behind, along with its coupon-only fields.
  useEffect(() => {
    if (isOneOffCreator && fulfillmentType === FULFILLMENT_TYPE_DEALS) {
      handleDeliveryMethodChange(FULFILLMENT_TYPE_SHIPPED);
    }
  }, [isOneOffCreator, fulfillmentType, handleDeliveryMethodChange]);

  return (
    <Container>
      <Title>
        <BodyText>Delivery method</BodyText>
        <HelpIcon helpText='Select how you want members to receive products. Ship products directly to members or send codes to members they can redeem to purchase products themselves.' />
      </Title>
      <FormFieldController
        control={control}
        name='fulfillmentOptions.fulfillmentType'
        rules={{ required: 'Please select a delivery method.' }}
      >
        {({ disabled, error, onChange, value, ...props }) => (
          <>
            <StyledRadioGroup
              {...props}
              disabled={isDisabled ?? disabled}
              onValueChange={(nextValue: FulfillmentType) => {
                handleDeliveryMethodChange(nextValue);
                onChange(nextValue);
              }}
              value={value}
            >
              {deliveryOptions.map((item) => (
                <RadioGroupItemWrapper key={item.value}>
                  <StyledRadioGroupItem
                    id={item.value}
                    trackingId={item.trackingId}
                    value={item.value}
                  >
                    <RadioGroup.Indicator />
                  </StyledRadioGroupItem>
                  <StyledRadioGroupLabel htmlFor={item.value}>
                    <BodyText>{item.label}</BodyText>
                  </StyledRadioGroupLabel>
                </RadioGroupItemWrapper>
              ))}
            </StyledRadioGroup>
            {error && (
              <ErrorText>
                <StyledIcon name='error' />
                <CaptionErrorText>{error.message}</CaptionErrorText>
              </ErrorText>
            )}
          </>
        )}
      </FormFieldController>
    </Container>
  );
};

const Container = styled.div`
  margin-top: ${({ theme }) => theme.spacings[2]};
`;

const Title = styled.div`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacings[2]};
`;

const StyledRadioGroup = styled(RadioGroup)`
  margin-top: ${({ theme }) => theme.spacings[1]};
  display: flex;
  gap: ${({ theme }) => theme.spacings[2]};
`;

const RadioGroupItemWrapper = styled.div`
  display: flex;
  align-items: center;
`;

const StyledRadioGroupItem = styled(RadioGroup.Item)`
  margin-right: ${({ theme }) => theme.spacings[1]};
`;

const StyledRadioGroupLabel = styled(RadioGroup.Label)`
  display: flex;
  align-items: center;
`;

const ErrorText = styled.div`
  display: flex;
  align-items: center;
`;

const StyledIcon = styled(Icon)(
  ({ theme }) => `
    margin-right: ${theme.spacings[1]};
    color: ${theme.colors.aliases.error};
    height: calc(${theme.spacings[1]} * 3);
    width: calc(${theme.spacings[1]} * 3);
`,
);
